import { useState } from 'react'
import {
  Zap, RefreshCw, ChevronRight, ChevronDown, CheckCircle, XCircle,
  Loader2, MessageSquare, Send, Play, AlertCircle, PauseCircle, Maximize2, RotateCcw
} from 'lucide-react'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import {
  usePipelineQueue, usePipelineItem, usePipelineApprove,
  usePipelineReject, usePipelinePause, usePipelineMessage, usePipelineTrigger, usePipelineRerun,
} from '@/hooks/useApi'
import type { PipelineItem, PipelineStageOutput } from '@/lib/api'
import { timeAgo } from '@/lib/utils'
import { StageDetailView } from './StageDetailView'
import { CopyButton } from './StageDetailView'

const STAGE_ORDER = ['idea', 'planning', 'design', 'build', 'approval']

const STATUS_BADGE: Record<string, string> = {
  awaiting_review: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  approved: 'bg-green-500/20 text-green-400 border-green-500/30',
  rejected: 'bg-red-500/20 text-red-400 border-red-500/30',
  running: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  failed: 'bg-red-500/20 text-red-400 border-red-500/30',
}

const ITEM_STATUS_BADGE: Record<string, string> = {
  in_progress: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  paused: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  completed: 'bg-green-500/20 text-green-400 border-green-500/30',
  failed: 'bg-red-500/20 text-red-400 border-red-500/30',
}

function StageOutputCard({
  stage,
  itemId,
  isActive,
  onOpenDetail,
}: {
  stage: PipelineStageOutput
  itemId: string
  isActive: boolean
  onOpenDetail: () => void
}) {
  const [expanded, setExpanded] = useState(isActive && stage.status === 'awaiting_review')
  const [note, setNote] = useState('')
  const [msgText, setMsgText] = useState('')
  const approve = usePipelineApprove()
  const reject = usePipelineReject()
  const pause = usePipelinePause()
  const sendMsg = usePipelineMessage()
  const rerun = usePipelineRerun()

  const parsed = stage.output_parsed as Record<string, any> | null
  const canReview = stage.status === 'awaiting_review'

  return (
    <div className="border border-border rounded-lg overflow-hidden">
      <button
        className="w-full flex items-center justify-between px-4 py-3 bg-surface hover:bg-surface-elevated text-left transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          {expanded ? <ChevronDown className="h-4 w-4 text-text-muted" /> : <ChevronRight className="h-4 w-4 text-text-muted" />}
          <span className="text-sm font-medium capitalize">{stage.stage}</span>
          <Badge className={`text-[10px] border ${STATUS_BADGE[stage.status] ?? 'bg-surface text-text-muted border-border'}`}>
            {stage.status.replace('_', ' ')}
          </Badge>
          <span className="text-[10px] text-text-muted">{stage.engine}</span>
        </div>
        <span className="text-[10px] text-text-muted">{timeAgo(new Date(stage.created_at))}</span>
      </button>

      {expanded && (
        <div className="border-t border-border bg-surface-elevated p-4 space-y-4">

          {parsed !== null && (
            <button
              onClick={onOpenDetail}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium bg-accent/15 text-accent border border-accent/30 hover:bg-accent/25 transition-colors"
            >
              <Maximize2 className="h-3 w-3" />
              View full {stage.stage}
            </button>
          )}

          {/* AI output summary */}
          {parsed !== null && (
            <div className="space-y-2">
              {parsed.verdict != null && (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-text-muted">Verdict:</span>
                  <Badge className={`text-[10px] border ${
                    String(parsed.verdict) === 'proceed' ? 'bg-green-500/20 text-green-400 border-green-500/30'
                    : String(parsed.verdict) === 'conflicts_found' ? 'bg-red-500/20 text-red-400 border-red-500/30'
                    : 'bg-amber-500/20 text-amber-400 border-amber-500/30'
                  }`}>
                    {String(parsed.verdict)}
                  </Badge>
                  {parsed.verdict_reason != null && (
                    <span className="text-xs text-text-muted">{String(parsed.verdict_reason)}</span>
                  )}
                </div>
              )}
              {Boolean(parsed.structured_writeup || parsed.plan_text || parsed.design_text) && (
                <div className="space-y-1">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] text-text-muted">Output</span>
                    <CopyButton text={String(parsed.structured_writeup ?? parsed.plan_text ?? parsed.design_text ?? '')} />
                  </div>
                  <div className="text-xs text-text-secondary bg-surface rounded p-3 max-h-48 overflow-y-auto font-mono whitespace-pre-wrap border border-border">
                    {String(parsed.structured_writeup ?? parsed.plan_text ?? parsed.design_text ?? '').slice(0, 2000)}
                  </div>
                </div>
              )}
              {Array.isArray(parsed.improvements) && parsed.improvements.length > 0 && (
                <div>
                  <div className="text-[10px] text-text-muted mb-1">Improvements</div>
                  <ul className="space-y-1">
                    {(parsed.improvements as string[]).slice(0, 5).map((imp, i) => (
                      <li key={i} className="text-xs text-text-secondary flex gap-2">
                        <span className="text-text-muted shrink-0">+</span>{imp}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {Array.isArray(parsed.conflicts) && parsed.conflicts.length > 0 && (
                <div>
                  <div className="text-[10px] text-text-muted mb-1">Conflicts</div>
                  <ul className="space-y-1">
                    {(parsed.conflicts as string[]).slice(0, 5).map((c, i) => (
                      <li key={i} className="text-xs text-red-400 flex gap-2">
                        <span className="shrink-0">!</span>{c}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Build stage: show raw job output */}
          {stage.stage === 'build' && parsed?.job_output && (
            <div className="text-xs font-mono bg-surface rounded p-3 max-h-48 overflow-y-auto whitespace-pre-wrap border border-border text-text-secondary">
              {String(parsed.job_output).slice(-3000)}
            </div>
          )}

          {/* Conversation thread */}
          {stage.conversations && stage.conversations.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-[10px] text-text-muted">Discussion</span>
                <CopyButton text={stage.conversations.map((c) => `[${c.role}] ${c.content}`).join('\n\n---\n\n')} />
              </div>
              {stage.conversations.map((c) => (
                <div
                  key={c.id}
                  className={`text-xs rounded px-3 py-2 ${
                    c.role === 'user'
                      ? 'bg-accent/10 border border-accent/20 text-text-primary ml-4'
                      : 'bg-surface border border-border text-text-secondary'
                  }`}
                >
                  <span className="text-[10px] text-text-muted mr-2">{c.role}</span>
                  {c.content}
                </div>
              ))}
            </div>
          )}

          {/* Message input (for awaiting_review stages) */}
          {canReview && (
            <div className="flex gap-2">
              <input
                className="flex-1 text-xs bg-surface border border-border rounded px-3 py-2 text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent"
                placeholder="Add a note before approving..."
                value={msgText}
                onChange={(e) => setMsgText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && msgText.trim()) {
                    sendMsg.mutate({ id: itemId, message: msgText.trim() })
                    setMsgText('')
                  }
                }}
              />
              <button
                className="p-2 rounded border border-border bg-surface hover:bg-surface-elevated text-text-muted hover:text-text-primary transition-colors disabled:opacity-40"
                onClick={() => { if (msgText.trim()) { sendMsg.mutate({ id: itemId, message: msgText.trim() }); setMsgText('') } }}
                disabled={sendMsg.isPending || !msgText.trim()}
              >
                <Send className="h-3.5 w-3.5" />
              </button>
            </div>
          )}

          {/* Approve / Reject / Re-review -- note on its own full-width row, buttons wrap
              below it. A single non-wrapping flex row here was clipping Reject and
              Re-review off the right edge on real phone widths -- confirmed via screenshot. */}
          {canReview && (
            <div className="space-y-2 pt-1">
              <input
                className="w-full text-xs bg-surface border border-border rounded px-3 py-2 text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent"
                placeholder={stage.stage === 'approval' ? 'Approval note (optional)...' : 'Approval note (optional)...'}
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  className="flex items-center gap-1.5 px-3 py-2 rounded text-xs font-medium bg-green-500/20 text-green-400 border border-green-500/30 hover:bg-green-500/30 transition-colors disabled:opacity-40"
                  onClick={() => approve.mutate({ id: itemId, note: note || undefined })}
                  disabled={approve.isPending}
                >
                  {approve.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle className="h-3.5 w-3.5" />}
                  {stage.stage === 'approval' ? 'Final Approve' : 'Approve'}
                </button>
                <button
                  className="flex items-center gap-1.5 px-3 py-2 rounded text-xs font-medium bg-red-500/20 text-red-400 border border-red-500/30 hover:bg-red-500/30 transition-colors disabled:opacity-40"
                  onClick={() => {
                    const msg = note.trim() || 'Rejected — needs revision'
                    reject.mutate({ id: itemId, message: msg })
                  }}
                  disabled={reject.isPending}
                >
                  {reject.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <XCircle className="h-3.5 w-3.5" />}
                  Reject
                </button>
                <button
                  className="flex items-center gap-1.5 px-3 py-2 rounded text-xs font-medium bg-blue-500/20 text-blue-400 border border-blue-500/30 hover:bg-blue-500/30 transition-colors disabled:opacity-40"
                  onClick={() => rerun.mutate({ id: itemId, stage: stage.stage, note: note.trim() || undefined })}
                  disabled={rerun.isPending}
                  title="Attach the note above (if any) and generate a fresh attempt at this stage -- doesn't approve or reject this one"
                >
                  {rerun.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />}
                  Re-review with notes
                </button>
                {stage.stage === 'idea' && (
                  <button
                    className="flex items-center gap-1.5 px-3 py-2 rounded text-xs font-medium bg-amber-500/20 text-amber-400 border border-amber-500/30 hover:bg-amber-500/30 transition-colors disabled:opacity-40"
                    onClick={() => {
                      const reason = note.trim() || 'Paused — pending further review'
                      pause.mutate({ id: itemId, reason })
                    }}
                    disabled={pause.isPending}
                  >
                    {pause.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <PauseCircle className="h-3.5 w-3.5" />}
                    Pause
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function PipelineItemDetail({ itemId }: { itemId: string }) {
  const { data, isLoading, error, refetch } = usePipelineItem(itemId)
  const [detailStage, setDetailStage] = useState<PipelineStageOutput | null>(null)

  if (isLoading) return (
    <div className="flex items-center justify-center h-32 text-text-muted">
      <Loader2 className="h-5 w-5 animate-spin" />
    </div>
  )
  if (error || !data?.item) return (
    <div className="text-xs text-red-400 px-4 py-3">Failed to load pipeline item.</div>
  )

  const { item, stages } = data
  const activeStage = stages.find(s => s.stage === item.current_stage && s.status === 'awaiting_review')

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm font-semibold text-text-primary">{item.idea_title}</div>
          <div className="text-[10px] text-text-muted mt-0.5">
            {item.id} &middot; stage: <span className="capitalize">{item.current_stage}</span> &middot; {timeAgo(new Date(item.created_at))}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge className={`text-[10px] border ${ITEM_STATUS_BADGE[item.status] ?? ''}`}>
            {item.status.replace('_', ' ')}
          </Badge>
          <button onClick={() => refetch()} className="p-1.5 rounded hover:bg-surface-elevated text-text-muted hover:text-text-primary transition-colors">
            <RefreshCw className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {/* Stage progress bar */}
      <div className="flex items-center gap-1">
        {STAGE_ORDER.map((s, i) => {
          const done = stages.find(st => st.stage === s && st.status === 'approved')
          const active = item.current_stage === s
          const rejected = stages.find(st => st.stage === s && st.status === 'rejected')
          return (
            <div key={s} className="flex items-center gap-1 flex-1">
              <div className={`flex-1 h-1.5 rounded-full transition-colors ${
                done ? 'bg-green-500' : rejected ? 'bg-red-500' : active ? 'bg-accent' : 'bg-surface-elevated'
              }`} />
              {i < STAGE_ORDER.length - 1 && <ChevronRight className="h-3 w-3 text-text-muted shrink-0" />}
            </div>
          )
        })}
      </div>
      <div className="flex gap-1 text-[9px] text-text-muted">
        {STAGE_ORDER.map(s => <div key={s} className="flex-1 text-center capitalize">{s}</div>)}
      </div>

      {/* Stage outputs */}
      <div className="space-y-2">
        {stages.length === 0 && (
          <div className="text-xs text-text-muted text-center py-4">No stage outputs yet.</div>
        )}
        {stages.map(stage => (
          <StageOutputCard
            key={stage.id}
            stage={stage}
            itemId={itemId}
            isActive={stage.id === activeStage?.id}
            onOpenDetail={() => setDetailStage(stage)}
          />
        ))}
      </div>

      {detailStage && (
        <StageDetailView
          itemTitle={item.idea_title}
          stage={detailStage}
          onClose={() => setDetailStage(null)}
        />
      )}
    </div>
  )
}

export function PipelinePanel() {
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [statusFilter, setStatusFilter] = useState('')
  const [triggerIdea, setTriggerIdea] = useState('')

  const { data, isLoading, refetch } = usePipelineQueue(statusFilter || undefined)
  const trigger = usePipelineTrigger()

  const items: PipelineItem[] = data?.items ?? []

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Zap className="h-4 w-4 text-accent" />
          <span className="text-sm font-semibold">Automation Pipeline</span>
          <Badge className="text-[10px] border border-border bg-surface text-text-muted">{data?.count ?? 0} items</Badge>
        </div>
        <div className="flex items-center gap-2">
          <select
            className="text-xs bg-surface border border-border rounded px-2 py-1.5 text-text-primary focus:outline-none focus:border-accent"
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
          >
            <option value="">All statuses</option>
            <option value="in_progress">In progress</option>
            <option value="paused">Paused</option>
            <option value="completed">Completed</option>
          </select>
          <button onClick={() => refetch()} className="p-1.5 rounded hover:bg-surface-elevated text-text-muted hover:text-text-primary transition-colors">
            <RefreshCw className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {/* Trigger row */}
      <div className="flex items-center gap-2 px-4 py-3 rounded-lg border border-border bg-surface">
        <Play className="h-4 w-4 text-text-muted shrink-0" />
        <span className="text-xs text-text-muted">Trigger idea worker</span>
        <input
          className="w-20 text-xs bg-surface-elevated border border-border rounded px-2 py-1 text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent"
          placeholder="idea #"
          value={triggerIdea}
          onChange={e => setTriggerIdea(e.target.value)}
          type="number"
        />
        <button
          className="flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium bg-accent/20 text-accent border border-accent/30 hover:bg-accent/30 transition-colors disabled:opacity-40"
          onClick={() => trigger.mutate(triggerIdea ? parseInt(triggerIdea) : undefined)}
          disabled={trigger.isPending}
        >
          {trigger.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Zap className="h-3 w-3" />}
          Run
        </button>
        {trigger.isSuccess && (
          <span className="text-[10px] text-green-400">Triggered</span>
        )}
        {trigger.isError && (
          <span className="text-[10px] text-red-400">Failed</span>
        )}
      </div>

      {/* Main layout: queue list + detail */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Queue */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle className="text-sm">Queue</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {isLoading && (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-5 w-5 animate-spin text-text-muted" />
              </div>
            )}
            {!isLoading && items.length === 0 && (
              <div className="flex flex-col items-center gap-2 py-8 text-text-muted">
                <AlertCircle className="h-5 w-5 opacity-40" />
                <span className="text-xs">No pipeline items</span>
              </div>
            )}
            {items.map(item => (
              <button
                key={item.id}
                className={`w-full text-left px-4 py-3 border-b border-border last:border-0 hover:bg-surface-elevated transition-colors ${selectedId === item.id ? 'bg-surface-elevated border-l-2 border-l-accent' : ''}`}
                onClick={() => setSelectedId(item.id)}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs font-medium text-text-primary truncate">{item.idea_title}</span>
                  <Badge className={`text-[10px] border shrink-0 ${ITEM_STATUS_BADGE[item.status] ?? ''}`}>
                    {item.status.replace('_', ' ')}
                  </Badge>
                </div>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-[10px] text-text-muted capitalize">{item.current_stage}</span>
                  <span className="text-[10px] text-text-muted">&middot;</span>
                  <span className="text-[10px] text-text-muted">{timeAgo(new Date(item.updated_at))}</span>
                  {!!item.previous_rejections && item.previous_rejections > 0 && (
                    <>
                      <span className="text-[10px] text-text-muted">&middot;</span>
                      <span className="text-[10px] text-red-400/80">
                        rejected {item.previous_rejections}x previously
                      </span>
                    </>
                  )}
                </div>
                {/* Mini stage dots */}
                <div className="flex gap-0.5 mt-1.5">
                  {STAGE_ORDER.map(s => (
                    <div
                      key={s}
                      className={`h-1 w-4 rounded-full ${
                        item.current_stage === s ? 'bg-accent'
                        : STAGE_ORDER.indexOf(s) < STAGE_ORDER.indexOf(item.current_stage) ? 'bg-green-500'
                        : 'bg-surface-elevated'
                      }`}
                    />
                  ))}
                </div>
              </button>
            ))}
          </CardContent>
        </Card>

        {/* Detail */}
        <Card className="lg:col-span-2">
          <CardContent className="p-4">
            {!selectedId ? (
              <div className="flex flex-col items-center gap-2 py-16 text-text-muted">
                <MessageSquare className="h-6 w-6 opacity-30" />
                <span className="text-sm">Select an item to review</span>
              </div>
            ) : (
              <PipelineItemDetail itemId={selectedId} />
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
